import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Text,
  Box,
  FormControl,
  FormLabel,
  Button,
  VStack,
  useToast,
  Input,
} from '@chakra-ui/react';

import { AttachmentIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import FileUploadCard from 'components/FileUploadCard';

import {
  apiUploadCommercialFile,
  apiGetCommercialFiles,
  apiDeleteCommercialFile,
} from 'api';
import { COMMERCIAL_FILE_TYPES } from 'constants/fileTypes';
import { useAppSelector } from 'hooks';
import type { APIApplicationFile } from 'types/api.types';

interface CommercialMVRUploadProps {
  isLocked?: boolean;
}

const CommercialMVRUpload = ({
  isLocked = false,
}: CommercialMVRUploadProps) => {
  const { uuid } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Get the selected insurance company from Redux
  const insuranceCompany = useAppSelector(
    (state) => state.commercialForm.data.insurance_company,
  );

  // Query for existing files (must be called before any conditional returns)
  const { data: files = [] } = useQuery({
    queryKey: ['commercial-files', uuid],
    queryFn: () => apiGetCommercialFiles(uuid!),
    refetchOnWindowFocus: false,
    enabled: !!uuid, // Only run query if uuid exists
  }) as { data: APIApplicationFile[] };

  // Upload mutation (must be called before any conditional returns)
  const uploadMutation = useMutation({
    mutationFn: ({ file, fileType }: { file: File; fileType: string }) =>
      apiUploadCommercialFile(uuid!, file, fileType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commercial-files', uuid] });
      toast({
        title: 'Success',
        description: 'MVR Report uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Upload failed',
        description:
          error?.response?.data?.error ||
          'Failed to upload file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  // Delete mutation (must be called before any conditional returns)
  const deleteMutation = useMutation({
    mutationFn: (fileId: number) => apiDeleteCommercialFile(uuid!, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commercial-files', uuid] });
      toast({
        title: 'File deleted',
        description: 'MVR Report removed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: () => {
      toast({
        title: 'Delete failed',
        description: 'Failed to delete file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  // Check if selected carrier supports MVR reports (Aviva Elite)
  const shouldShowMVR = insuranceCompany === 'Aviva Elite';

  // Filter for MVR report files
  const mvrFiles = files.filter(
    (file) => file.file_type === COMMERCIAL_FILE_TYPES.MVR_REPORT,
  );

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    uploadMutation.mutate({ file, fileType: 'mvr' });

    // Clear the input
    event.target.value = '';
  };

  const handleRemoveFile = (fileId: number) => {
    deleteMutation.mutate(fileId);
  };

  // Conditionally render content instead of early return
  if (!shouldShowMVR) {
    return null;
  }

  return (
    <Card mb="20px" id="sectionCommercialMVRUpload">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        MVR Upload (per driver)
      </Text>

      <VStack spacing={4} align="stretch">
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="500" ml="0">
            Motor Vehicle Records (MVR)
          </FormLabel>

          <VStack spacing={3} align="stretch">
            <Box>
              <Input
                type="file"
                onChange={handleFileSelect}
                display="none"
                id="mvr-upload"
                disabled={uploadMutation.isPending || isLocked}
                accept=".pdf,.doc,.docx"
              />
              <label htmlFor="mvr-upload">
                <Button
                  as="span"
                  leftIcon={<AttachmentIcon />}
                  colorScheme="blue"
                  variant="outline"
                  size="md"
                  isLoading={uploadMutation.isPending}
                  loadingText="Uploading..."
                  cursor="pointer"
                  width="full"
                  maxW="300px"
                  isDisabled={isLocked}
                >
                  Choose File
                </Button>
              </label>
            </Box>

            {mvrFiles.map((file) => (
              <FileUploadCard
                key={file.id}
                file={file}
                badgeText="MVR Report"
                badgeColorScheme="blue"
                onDelete={handleRemoveFile}
                isDeleting={deleteMutation.isPending}
                isLocked={isLocked}
              />
            ))}
          </VStack>
        </FormControl>
      </VStack>
    </Card>
  );
};

export default CommercialMVRUpload;
